import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap, BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ProfileService } from '../services/profile/profile.service';
import { Router } from '@angular/router';
import { JwtService } from '../services/jwt/jwt.service';

interface LoginResponse {
  accessToken: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly isAuthenticated = signal<boolean>(false);
  private readonly isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private readonly TOKEN_KEY = 'access_token';
  
  private sessionTimeoutId: number | null = null;
  private sessionCheckIntervalId: number | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly profileService: ProfileService,
    private readonly router: Router,
    private readonly jwtService: JwtService
  ) {
    // Initialize authentication state based on token presence
    this.checkAuthStatus();
  }

  private checkAuthStatus(): void {
    // Check if access_token exists in localStorage
    const token = localStorage.getItem(this.TOKEN_KEY);
    const hasToken = token !== null && token !== '';
    
    this.isAuthenticated.set(hasToken);
    this.isAuthenticatedSubject.next(hasToken);
    
    if (hasToken) {
      this.profileService.refreshProfile();
      // This will handle starting the timeout
      this.checkSessionValidity(); 
      // Start periodic session checks
      this.startSessionCheckInterval(); 
    } else {
      this.stopSessionCheckInterval();
    }
  }

  private startSessionTimeout(): void {
    this.clearSessionTimeout();
    
    const token = this.getToken();
    if (!token) {
      return;
    }

    const expirationTime = this.jwtService.getTokenExpirationTime(token);
    if (!expirationTime) {
      // If we can't parse the token, fall back to a default timeout
      this.sessionTimeoutId = window.setTimeout(() => {
        this.autoLogout();
      }, 60 * 60 * 1000); // 1 hour default
      return;
    }

    const now = Date.now();
    const timeUntilExpiration = expirationTime - now;

    if (timeUntilExpiration <= 0) {
      // Token is already expired
      this.autoLogout();
      return;
    }

    this.sessionTimeoutId = window.setTimeout(() => {
      this.autoLogout();
    }, timeUntilExpiration);
  }

  private clearSessionTimeout(): void {
    if (this.sessionTimeoutId) {
      clearTimeout(this.sessionTimeoutId);
      this.sessionTimeoutId = null;
    }
  }

  private checkSessionValidity(): void {
    const token = localStorage.getItem(this.TOKEN_KEY);
    
    // If no token, no need to check session validity
    if (!token) {
      return;
    }

    const expirationTime = this.jwtService.getTokenExpirationTime(token);
    if (!expirationTime) {
      // If we can't parse the token, consider it invalid
      this.autoLogout();
      return;
    }

    const now = Date.now();
    if (now >= expirationTime) {
      // Token has expired
      this.autoLogout();
      return;
    }

    // Token is still valid, restart timeout with remaining time
    const timeUntilExpiration = expirationTime - now;
    this.sessionTimeoutId = window.setTimeout(() => {
      this.autoLogout();
    }, timeUntilExpiration);
  }

  private autoLogout(): void {
    this.logout();
    this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  private setToken(token: string): void {
    // Store the new token
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  private clearToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
  }

  login(username: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${environment.apiURL}/auth/login`, {
      username,
      password
    }, {
      withCredentials: true, // Important for cookies
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    }).pipe(
      tap((response: LoginResponse) => {
        // Set the token from the response
        if (response.accessToken) {
          // Clear any existing session timeout first
          this.clearSessionTimeout();
          
          // Store token in localStorage
          this.setToken(response.accessToken);
          
          // Verify the token was stored
          const storedToken = this.getToken();
          
          if (storedToken) {
            this.isAuthenticated.set(true);
            this.isAuthenticatedSubject.next(true);
            this.profileService.refreshProfile();
            this.startSessionTimeout();
            // Start periodic session checks after successful login
            this.startSessionCheckInterval(); 
          } else {
            this.isAuthenticated.set(false);
            this.isAuthenticatedSubject.next(false);
          }
        } else {
          this.isAuthenticated.set(false);
          this.isAuthenticatedSubject.next(false);
        }
      })
    );
  }

  get LoginStatus(): boolean {
    return this.isAuthenticated();
  }

  get LoginStatus$(): Observable<boolean> {
    return this.isAuthenticatedSubject.asObservable();
  }

  logout(): void {
    // Clear the authentication state
    this.isAuthenticated.set(false);
    this.isAuthenticatedSubject.next(false);
    // Clear token from localStorage
    this.clearToken();
    // Clear session timeout
    this.clearSessionTimeout();
    // Stop periodic session checks
    this.stopSessionCheckInterval();
  }

  getRemainingSessionTime(): number {
    const token = this.getToken();
    if (!token) {
      return 0;
    }

    return this.jwtService.getRemainingTime(token);
  }

  isSessionValid(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    return !this.jwtService.isTokenExpired(token);
  }

  private startSessionCheckInterval(): void {
    this.stopSessionCheckInterval();
    
    // Check session validity every 5 seconds
    this.sessionCheckIntervalId = window.setInterval(() => {
      if (this.LoginStatus && !this.isSessionValid()) {
        this.autoLogout();
      }
    }, 5000);
  }

  private stopSessionCheckInterval(): void {
    if (this.sessionCheckIntervalId) {
      clearInterval(this.sessionCheckIntervalId);
      this.sessionCheckIntervalId = null;
    }
  }
}